export enum PamsCalendarTypeEnum{
    Task,
    Visit,
    Meeting,
    Email,
    Call,
    BiddueDate,
    Receivable,
    Deliverable,
    ActivityRangeEnd = 9,
}
export class PamsCalendarResponse {
  Active:PamsCalendarResponseDetails[];
  Removed:PamsCalendarResponseDetails[];
}
export class PamsCalendarResponseDetails {
  CompanyBranchID: number;
  OwnerName: string;
  PriorityColor: string;
  PriorityID: number;
  endDate: Date;
  id: number;
  rowId: number;
  owner: string;
  startDate: Date;
  title: string;
  ClientName: string;
  type: number;
}
// ============================================================================
// Microsoft Graph Calendar Event Models
// ============================================================================

/**
 * Main Calendar Event class for creating/updating appointments
 */
export class OutlookCalendarEvent {
  '@odata.etag'?: string; 
  subject: string; // Required - Meeting title
  body?: EventBody;
  start: DateTimeTimeZone; // Required
  end: DateTimeTimeZone; // Required
  transactionId: string; // Read-only
  isAllDay?: boolean;
  id?: string; // Read-only, assigned by Microsoft Graph
  location?: Location;
  locations?: Location[]; // Multiple locations
  attendees?: Attendee[];
  organizer?: Recipient; // Read-only in most cases
  recurrence?: PatternedRecurrence;
  importance?: Importance;
  sensitivity?: Sensitivity;
  showAs?: FreeBusyStatus;
  isCancelled?: boolean; // Read-only
  isOrganizer?: boolean; // Read-only
  responseRequested?: boolean;
  seriesMasterId?: string; // For recurring events
  categories?: string[];
  webLink?: string; // Read-only
  onlineMeetingUrl?: string; // Teams/Skype meeting URL
  isOnlineMeeting?: boolean;
  onlineMeetingProvider?: OnlineMeetingProviderType;
  allowNewTimeProposals?: boolean;
  hideAttendees?: boolean;
  responseStatus?: ResponseStatus; // Read-only
  reminderMinutesBeforeStart?: number;
  isReminderOn?: boolean;
  hasAttachments?: boolean; // Read-only
  createdDateTime?: string; // Read-only
  lastModifiedDateTime?: string; // Read-only
}

/**
 * Date and time with timezone information
 */
export class DateTimeTimeZone {
  dateTime: string; // ISO 8601 format: "2024-01-15T14:30:00.0000000"
  timeZone: string; // IANA timezone: "America/New_York" or "UTC"
}

/**
 * Event body content
 */
export class EventBody {
  contentType: BodyType;
  content: string;
}

export type BodyType = 'text' | 'html';

/**
 * Location information
 */
export class Location {
  displayName?: string;
  address?: PhysicalAddress;
  coordinates?: OutlookGeoCoordinates;
  locationEmailAddress?: string;
  locationType?: LocationType;
  locationUri?: string;
  uniqueId?: string;
  uniqueIdType?: LocationUniqueIdType;
}

export class PhysicalAddress {
  street?: string;
  city?: string;
  state?: string;
  countryOrRegion?: string;
  postalCode?: string;
}

export class OutlookGeoCoordinates {
  altitude?: number;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  altitudeAccuracy?: number;
}

export type LocationType = 'default' | 'conferenceRoom' | 'homeAddress' | 'businessAddress' | 'geoCoordinates' | 'streetAddress' | 'hotel' | 'restaurant' | 'localBusiness' | 'postalAddress';

export type LocationUniqueIdType = 'unknown' | 'locationStore' | 'directory' | 'private' | 'bing';

/**
 * Attendee information
 */
export class Attendee {
  type?: AttendeeType;
  status?: ResponseStatus;
  emailAddress: EmailAddress; // Required
}

export class EmailAddress {
  address: string; // Required - email address
  name?: string; // Display name
}

export type AttendeeType = 'required' | 'optional' | 'resource';

export class ResponseStatus {
  response?: ResponseType;
  time?: string; // ISO 8601 format
}

export type ResponseType = 'none' | 'organizer' | 'tentativelyAccepted' | 'accepted' | 'declined' | 'notResponded';

/**
 * Organizer/Recipient information
 */
export class Recipient {
  emailAddress: EmailAddress;
}

/**
 * Recurrence pattern for recurring events
 */
export class PatternedRecurrence {
  pattern: RecurrencePattern;
  range: RecurrenceRange;
}

export class RecurrencePattern {
  type: RecurrencePatternType; // Required
  interval: number; // Required - every N days/weeks/months/years
  month?: number; // 1-12
  dayOfMonth?: number; // 1-31
  daysOfWeek?: DayOfWeek[];
  firstDayOfWeek?: DayOfWeek;
  index?: WeekIndex; // first, second, third, fourth, last
}

export type RecurrencePatternType = 'daily' | 'weekly' | 'absoluteMonthly' | 'relativeMonthly' | 'absoluteYearly' | 'relativeYearly';

export type DayOfWeek = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

export type WeekIndex = 'first' | 'second' | 'third' | 'fourth' | 'last';

export class RecurrenceRange {
  type: RecurrenceRangeType; // Required
  startDate: string; // Required - YYYY-MM-DD format
  endDate?: string; // YYYY-MM-DD format
  recurrenceTimeZone?: string;
  numberOfOccurrences?: number;
}

export type RecurrenceRangeType = 'endDate' | 'noEnd' | 'numbered';

/**
 * Enums for various properties
 */
export type Importance = 'low' | 'normal' | 'high';

export type Sensitivity = 'normal' | 'personal' | 'private' | 'confidential';

export type FreeBusyStatus = 'unknown' | 'free' | 'tentative' | 'busy' | 'oof' | 'workingElsewhere';

export type OnlineMeetingProviderType = 'unknown' | 'skypeForBusiness' | 'skypeForConsumer' | 'teamsForBusiness';

// ============================================================================
// Calendar Event Creation/Update Helpers
// ============================================================================

/**
 * Simplified class for creating calendar events
 */
export class CreateEventRequest {
  subject: string;
  start: {
    dateTime: string | Date;
    timeZone?: string;
  };
  end: {
    dateTime: string | Date;
    timeZone?: string;
  };
  body?: {
    contentType?: 'text' | 'html';
    content: string;
  };
  location?: {
    displayName: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      countryOrRegion?: string;
    };
  };
  attendees?: {
    emailAddress: {
      address: string;
      name?: string;
    };
    type?: 'required' | 'optional' | 'resource';
  }[];
  isAllDay?: boolean;
  importance?: 'low' | 'normal' | 'high';
  showAs?: 'free' | 'tentative' | 'busy' | 'oof' | 'workingElsewhere';
  sensitivity?: 'normal' | 'personal' | 'private' | 'confidential';
  reminderMinutesBeforeStart?: number;
  isReminderOn?: boolean;
  categories?: string[];
  isOnlineMeeting?: boolean;
  onlineMeetingProvider?: 'teamsForBusiness' | 'skypeForBusiness';
  allowNewTimeProposals?: boolean;
  responseRequested?: boolean;
  recurrence?: {
    pattern: {
      type: 'daily' | 'weekly' | 'absoluteMonthly' | 'relativeMonthly' | 'absoluteYearly' | 'relativeYearly';
      interval: number;
      daysOfWeek?: ('sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday')[];
      dayOfMonth?: number;
      month?: number;
    };
    range: {
      type: 'endDate' | 'noEnd' | 'numbered';
      startDate: string; // YYYY-MM-DD
      endDate?: string; // YYYY-MM-DD
      numberOfOccurrences?: number;
    };
  };
}

/**
 * Response class when retrieving calendar events
 */
export class CalendarEventResponse {
  '@odata.context'?: string;
  value?: OutlookCalendarEvent[];
  '@odata.nextLink'?: string; // For pagination
}

/**
 * Query parameters for retrieving calendar events
 */
export class CalendarQuery {
  startDateTime?: string; // ISO 8601 format
  endDateTime?: string; // ISO 8601 format
  $select?: string; // Comma-separated list of properties
  $filter?: string; // OData filter
  $orderby?: string; // Sort order
  $top?: number; // Number of results
  $skip?: number; // Skip results for pagination
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Helper function to format dates for Microsoft Graph
 */
export function formatDateTimeForGraph(date: Date, timeZone?: string): DateTimeTimeZone {
  return {
    dateTime: date.toISOString().slice(0, -1) + '0000000', // Remove 'Z' and add precision
    timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
  };
}

/**
 * Helper function to create a simple calendar event
 */
export function createSimpleEvent(
  subject: string,
  startDate: Date,
  endDate: Date,
  location?: string,
  attendees?: string[],
  body?: string
): CreateEventRequest {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  const event: CreateEventRequest = {
    subject,
    start: {
      dateTime: startDate.toISOString(),
      timeZone
    },
    end: {
      dateTime: endDate.toISOString(),
      timeZone
    }
  };

  if (body) {
    event.body = {
      contentType: 'html',
      content: body
    };
  }

  if (location) {
    event.location = {
      displayName: location
    };
  }

  if (attendees && attendees.length > 0) {
    event.attendees = attendees.map(email => ({
      emailAddress: {
        address: email
      },
      type: 'required' as AttendeeType
    }));
  }

  return event;
}

/**
 * Helper function to create all-day event
 */
export function createAllDayEvent(
  subject: string,
  date: Date,
  location?: string,
  attendees?: string[]
): CreateEventRequest {
  const startDate = new Date(date);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(date);
  endDate.setHours(23, 59, 59, 999);

  return {
    ...createSimpleEvent(subject, startDate, endDate, location, attendees),
    isAllDay: true
  };
}

/**
 * Helper function to create recurring weekly meeting
 */
export function createWeeklyRecurringEvent(
  subject: string,
  startDate: Date,
  duration: number, // in minutes
  dayOfWeek: DayOfWeek,
  numberOfOccurrences: number,
  location?: string,
  attendees?: string[]
): CreateEventRequest {
  const endDate = new Date(startDate.getTime() + (duration * 60 * 1000));
  
  const baseEvent = createSimpleEvent(subject, startDate, endDate, location, attendees);
  
  baseEvent.recurrence = {
    pattern: {
      type: 'weekly',
      interval: 1,
      daysOfWeek: [dayOfWeek]
    },
    range: {
      type: 'numbered',
      startDate: startDate.toISOString().split('T')[0], // YYYY-MM-DD
      numberOfOccurrences
    }
  };

  return baseEvent;
}